import { useState } from 'react';
import { useLanguage } from '@/lib/i18n';
import { Menu, X } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import logo from '@/assets/logo.png';

const Navbar = () => {
  const { lang, setLang, tr } = useLanguage();
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const navLinks = [
    { label: tr('nav.nurses'), href: '#nurses' },
    { label: tr('nav.employers'), href: '#employers' },
    { label: tr('nav.about'), href: '#about' },
    { label: tr('nav.process'), href: '#westbalkan-process-popup' },
    { label: lang === 'de' ? 'Karriere' : lang === 'bs' ? 'Karijera' : 'Careers', href: '/karriere', type: 'route' },
    { label: tr('nav.contact'), href: '#contact-form' },
  ];

  const scrollTo = (id: string) => {
    setMenuOpen(false);
    if (location.pathname !== '/') {
      window.location.href = '/' + id;
      return;
    }
    // Small delay to allow mobile menu to close first
    setTimeout(() => {
      const el = document.querySelector(id);
      if (el) {
        const navHeight = 80;
        const top = el.getBoundingClientRect().top + window.scrollY - navHeight;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    }, 100);
  };

  const handleNavClick = (link: any) => {
    setMenuOpen(false);
    if (link.type === 'route') {
      navigate(link.href);
    } else if (link.href === '#westbalkan-process-popup') {
      const event = new CustomEvent('openWestbalkanProcessPopup');
      window.dispatchEvent(event);
    } else {
      scrollTo(link.href);
    }
  };

  const handleLogoClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    setMenuOpen(false);
    if (location.pathname === '/') {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-card/95 backdrop-blur-md border-b border-border">
      <div className="container mx-auto px-4 flex items-center justify-between h-16 md:h-20">
        <Link to="/" onClick={handleLogoClick} className="flex items-center gap-2">
          <img src={logo} alt="ViaMedica Balkan Logo" className="h-12 md:h-14 w-auto" />
          <span className="font-heading text-xl md:text-2xl font-bold text-primary tracking-tight">ViaMedica Balkan</span>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden lg:flex items-center gap-6">
          {navLinks.map(link => (
            <button
              key={link.href}
              onClick={() => handleNavClick(link)}
              className="text-sm font-medium text-foreground/80 hover:text-primary transition-colors"
            >
              {link.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 bg-muted rounded-full p-1">
            <button
              onClick={() => setLang('de')}
              aria-label="Deutsch"
              className={`px-2.5 py-1 text-xs md:text-sm rounded-full font-medium transition-all ${lang === 'de' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
            >
              DE
            </button>
            <button
              onClick={() => setLang('bs')}
              aria-label="Bosanski"
              className={`px-2.5 py-1 text-xs md:text-sm rounded-full font-medium transition-all ${lang === 'bs' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
            >
              BS
            </button>
            <button
              onClick={() => setLang('en')}
              aria-label="English"
              className={`px-2.5 py-1 text-xs md:text-sm rounded-full font-medium transition-all ${lang === 'en' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
            >
              EN
            </button>
          </div>

          {/* Mobile toggle */}
          <button onClick={() => setMenuOpen(!menuOpen)} className="lg:hidden text-foreground p-2">
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-card border-b border-border overflow-hidden"
          >
            <div className="px-4 py-4 flex flex-col gap-3">
              {navLinks.map(link => (
                <button
                  key={link.href}
                  onClick={() => handleNavClick(link)}
                  className="text-left text-foreground/80 hover:text-primary transition-colors py-2"
                >
                  {link.label}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
